import React, { useState } from 'react';
import { LayoutGrid, LayoutGridItem } from '../../../../../layout/LayoutGrid';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GridColumns } from '@mui/x-data-grid-pro';
import TimelineIcon from '@mui/icons-material/Timeline';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { useDispatch, useSelector } from 'react-redux';
import { selectChangeHistory } from '../../../../../../store/redux-store/placement-details/selectors';
import { placementDetailsAction } from '../../../../../../store/redux-store/placement-details/slice';
import { XGridDefaultColumn } from 'app/components/Common/XGrid/cells/XGridDefaults';
import { xGridDateSort } from 'app/components/Common/XGrid/sorting/XGridSorting';
import { XGridChangeHistoryOverflowCell } from 'app/components/Common/XGrid/cells/XGridChangeHistoryOverflowCell';
import { XGridHistoricalHoverView } from 'app/components/Common/XGrid/cells/HoverViewRenderers/XGridHistoricalHoverView';
import { usePromiseTracker } from 'react-promise-tracker';
import { useTranslation } from 'react-i18next';
import { ChangeHistoryTimelineModal } from '@AMIEWEB/Common/ChangeHistoryTimeline';
import type { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';

const useStyles = makeStyles()({
  container: {
    padding: '24px 0px',
  },
});

export const ChangeHistoryTab = ({ placementId }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const rows = useSelector(selectChangeHistory);
  const [columns, setColumns] = useState<GridColumns>(changeHistoryColumns());
  const { promiseInProgress } = usePromiseTracker({ area: 'placement-change-history' });
  const dispatch = useDispatch();
  const [timelineModalOpen, setTimelineModalOpen] = React.useState(false);

  const [sortModel, setSortModel] = React.useState<any>([
    {
      field: 'created',
      sort: 'desc',
    },
  ]);

  const rightActions: IXGridBulkActionButton[] = React.useMemo(() => {
    if (!rows?.length) return [];
    return [
      {
        KeyId: 'timeline-visualization',
        Icon: TimelineIcon,
        tooltipProps: {
          tooltipContent: t('placement.changeHistoryTab.timelineVisualization'),
          disabled: false,
        },
        onClick: () => setTimelineModalOpen(true),
      },
    ];
  }, [rows?.length, t]);

  const fetchPlacementChangeHistoryData = () => {
    dispatch(placementDetailsAction.getChangeHistory({ placementId, pageSize: 1000, pageNumber: 1 }));
  };

  React.useEffect(() => {
    fetchPlacementChangeHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LayoutGrid>
      <LayoutGridItem xs={12}>
        <Box className={classes.container}>
          <XGrid
            columns={columns}
            rows={rows}
            headerHeight={45}
            autoHeight
            disableSelectionOnClick
            loading={promiseInProgress}
            sortModel={sortModel}
            onSortModelChange={model => setSortModel(model)}
            toolbarProps={{
              refreshButton: true,
              refreshGrid: () => fetchPlacementChangeHistoryData(),
              resetColumns: () => setColumns(changeHistoryColumns()),
              rightActions,
            }}
          />
          <ChangeHistoryTimelineModal
            open={timelineModalOpen}
            onClose={() => setTimelineModalOpen(false)}
            title={t('placement.changeHistoryTab.timelineModalTitle')}
            historyData={rows ?? []}
          />
        </Box>
      </LayoutGridItem>
    </LayoutGrid>
  );
};

const changeHistoryColumns = (): GridColumns => [
  {
    ...XGridDefaultColumn,
    field: 'fieldChanged',
    headerName: 'Field',
  },
  {
    ...XGridDefaultColumn,
    field: 'oldValue',
    headerName: 'Old Value',
    renderCell: XGridChangeHistoryOverflowCell(XGridHistoricalHoverView)(),
    sortable: false,
  },
  {
    ...XGridDefaultColumn,
    field: 'newValue',
    headerName: 'New Value',
    renderCell: XGridChangeHistoryOverflowCell(XGridHistoricalHoverView)(),
    sortable: false,
  },
  {
    ...XGridDefaultColumn,
    field: 'userName',
    headerName: 'Changed By',
  },
  {
    ...XGridDefaultColumn,
    field: 'created',
    headerName: 'Date',
    type: 'date',
    sortComparator: xGridDateSort,
  },
  {
    ...XGridDefaultColumn,
    field: 'externalSystem',
    headerName: 'Source System',
    sortable: false,
  },
];
